import { useState, useEffect, useMemo } from "react";
import subscriptionHistoryService from "../services/subscriptionHistoryService";
import { useAuth } from "../context/AuthContext";
import {
  isBasicPackageName,
  isPremiumPackageName,
  packageIncludesPathModule,
  resolveSubscriptionPackageName,
} from "../utils/attendanceFlow";

// Module-level cache so the many components that call this hook on the same
// page (Navbar, Sidebar, and often several page-level components too) share
// one network call instead of each firing its own — mirrors the cache
// pattern already used by getAllDoctors in doctorService.js.
const FIVE_MINUTES_MS = 5 * 60 * 1000;
let __subscriptionCache = { organizationId: null, data: null, timestamp: 0, inFlight: null };

export const invalidateSubscriptionCapabilitiesCache = () => {
  __subscriptionCache = { organizationId: null, data: null, timestamp: 0, inFlight: null };
};

const fetchActiveSubscriptionCached = (organizationId) => {
  const now = Date.now();
  if (
    __subscriptionCache.organizationId === organizationId &&
    __subscriptionCache.data !== null &&
    now - __subscriptionCache.timestamp < FIVE_MINUTES_MS
  ) {
    return Promise.resolve(__subscriptionCache.data);
  }

  if (__subscriptionCache.inFlight && __subscriptionCache.organizationId === organizationId) {
    return __subscriptionCache.inFlight;
  }

  const request = subscriptionHistoryService
    .getActiveSubscription(organizationId)
    .then((subscription) => {
      __subscriptionCache = {
        organizationId,
        data: subscription,
        timestamp: Date.now(),
        inFlight: null,
      };
      return subscription;
    })
    .catch((error) => {
      __subscriptionCache.inFlight = null;
      throw error;
    });

  __subscriptionCache = { organizationId, data: null, timestamp: 0, inFlight: request };
  return request;
};

/**
 * Custom hook to check subscription capabilities
 * Returns capabilities array and helper functions to check specific capabilities
 */
export const useSubscriptionCapabilities = () => {
  const { user } = useAuth();
  const [capabilities, setCapabilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSubscription, setActiveSubscription] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const loadCapabilities = async () => {
      let organizationId = user?.organizationId;
      if (!organizationId) {
        const storedUser = localStorage.getItem("user");
        if (storedUser) {
          try {
            const parsedUser = JSON.parse(storedUser);
            organizationId = parsedUser?.organizationId;
          } catch (e) {
            console.error("[useSubscriptionCapabilities] Error parsing stored user:", e);
          }
        }
        if (!organizationId) {
          const orgIdFromStorage = localStorage.getItem("organizationId");
          if (orgIdFromStorage) {
            organizationId = Number(orgIdFromStorage);
          }
        }
      }

      if (!organizationId) {
        if (!cancelled) {
          setCapabilities([]);
          setActiveSubscription(null);
          setLoading(false);
        }
        return;
      }

      try {
        if (!cancelled) setLoading(true);
        const subscription = await fetchActiveSubscriptionCached(organizationId);
        if (cancelled) return;

        if (!subscription) {
          setCapabilities([]);
          setActiveSubscription(null);
          setLoading(false);
          return;
        }

        setActiveSubscription(subscription);

        let capabilitiesArray = [];

        if (subscription.subscriptionType) {
          const subscriptionType = subscription.subscriptionType;

          if (typeof subscriptionType.capabilities === "string") {
            try {
              capabilitiesArray = JSON.parse(subscriptionType.capabilities);
            } catch (e) {
              console.error("Error parsing capabilities JSON:", e);
              capabilitiesArray = [];
            }
          } else if (Array.isArray(subscriptionType.capabilities)) {
            capabilitiesArray = subscriptionType.capabilities;
          }
        } else if (subscription.capabilities) {
          if (typeof subscription.capabilities === "string") {
            try {
              capabilitiesArray = JSON.parse(subscription.capabilities);
            } catch (e) {
              console.error("Error parsing capabilities JSON:", e);
              capabilitiesArray = [];
            }
          } else if (Array.isArray(subscription.capabilities)) {
            capabilitiesArray = subscription.capabilities;
          }
        }

        setCapabilities(capabilitiesArray || []);
      } catch (error) {
        if (cancelled) return;
        console.error("Error loading subscription capabilities:", error);
        setCapabilities([]);
        setActiveSubscription(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadCapabilities();

    return () => {
      cancelled = true;
    };
  }, [user?.organizationId, user?.roleName]);

  const subscriptionPackage = useMemo(
    () => resolveSubscriptionPackageName(activeSubscription),
    [activeSubscription]
  );

  const isPremiumPackage = isPremiumPackageName(subscriptionPackage);
  const isBasicPackage = isBasicPackageName(subscriptionPackage);
  // Path/Pathology modules: Intermediate + Premium only
  const hasPathPackageAccess = packageIncludesPathModule(subscriptionPackage);

  const hasCapability = (capabilityName) => {
    if (!capabilityName || !Array.isArray(capabilities)) {
      return false;
    }

    const searchTerm = String(capabilityName).toLowerCase().trim();

    // Basic package never includes Path / Pathology features
    if (isBasicPackage && searchTerm.includes("path")) {
      return false;
    }

    return capabilities.some((cap) => {
      const capString = String(cap).toLowerCase();
      return capString === searchTerm || capString.includes(searchTerm);
    });
  };

  const hasAnyCapability = (capabilityNames) => {
    if (!Array.isArray(capabilityNames) || capabilityNames.length === 0) {
      return false;
    }
    return capabilityNames.some((name) => hasCapability(name));
  };

  const hasAllCapabilities = (capabilityNames) => {
    if (!Array.isArray(capabilityNames) || capabilityNames.length === 0) {
      return false;
    }
    return capabilityNames.every((name) => hasCapability(name));
  };

  return {
    capabilities,
    loading,
    activeSubscription,
    subscriptionPackage,
    isPremiumPackage,
    isBasicPackage,
    hasPathPackageAccess,
    hasCapability,
    hasAnyCapability,
    hasAllCapabilities,
  };
};

export default useSubscriptionCapabilities;
